/**
 * Shared org-role hooks.
 *
 * Lives in `@pc/ui` (rather than per-app) so every shell, module page,
 * and slot agrees on the same React-Query cache entry for "what role
 * does this user have in the active org?". The matching pure
 * predicates (`canWriteRole`, `isAdminRole`, `isViewerRole`) live in
 * `@pc/db/services/integrations` and are re-exported from `@pc/db`.
 *
 * The boundary that defines "writer" mirrors the DB helper
 * `core.user_can_write_org` from migration
 * `20260702060000_role_writer_gate.sql`. Any change to which roles
 * count as writers MUST land in BOTH places.
 */
import { useQuery } from '@tanstack/react-query';
import {
  canWriteRole,
  getMyRoleInOrg,
  isAdminRole,
  isViewerRole,
  type OrgRole,
} from '@pc/db';

/**
 * Fetch the current user's role in `orgId`. If `orgId` is omitted (or
 * passed `null` / `undefined`), reads `pc.activeOrgId` from
 * `localStorage` — the same key modules read via their `useActiveCtx`
 * adapter — so module pages can call this hook without threading an
 * org id from the host.
 *
 * Cache: keyed by `orgId`. `staleTime` is one minute because role
 * changes are rare; pages that need stricter freshness (Settings →
 * Team) can refetch explicitly.
 */
export function useMyOrgRole(orgId?: string | null) {
  const effectiveOrgId = orgId ?? readActiveOrgId();
  return useQuery({
    queryKey: ['orgRole', effectiveOrgId ?? null],
    queryFn: () =>
      effectiveOrgId ? getMyRoleInOrg(effectiveOrgId) : Promise.resolve(null),
    enabled: Boolean(effectiveOrgId),
    staleTime: 60_000,
  });
}

/**
 * Returns `true` when the current user is permitted to INSERT / UPDATE /
 * DELETE operational rows in `orgId` (owner / admin / operator). While
 * the role query is loading we return `false` so action buttons stay
 * hidden until we know the answer — that's the safer default than
 * flashing a button the user will then be denied at the DB layer.
 */
export function useCanWriteOrg(orgId?: string | null): boolean {
  const { data: role, isFetched } = useMyOrgRole(orgId);
  if (!isFetched) return false;
  return canWriteRole(role);
}

/** Returns `true` when the current user is `owner` / `admin`. */
export function useIsOrgAdmin(orgId?: string | null): boolean {
  const { data: role, isFetched } = useMyOrgRole(orgId);
  if (!isFetched) return false;
  return isAdminRole(role);
}

/** Returns `true` when the current user is `viewer`. */
export function useIsViewer(orgId?: string | null): boolean {
  const { data: role, isFetched } = useMyOrgRole(orgId);
  if (!isFetched) return false;
  return isViewerRole(role);
}

/** Re-export pure helpers for ergonomic single-import sites. */
export { canWriteRole, isAdminRole, isViewerRole };
export type { OrgRole };

function readActiveOrgId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem('pc.activeOrgId');
  } catch {
    return null;
  }
}
